import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import {
  Button, Chip, Input, Loading, Row,
} from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';
import PickLocationModal from './PickLocationModal';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import { requestLocationPermission, getCurrentPosition } from '../../services/location';
import { startOnlinePayment } from '../../services/payment';
import { colors } from '../../theme';
import { currency } from '../../utils/format';

const VEHICLE_ICON = {
  bike: '🏍️', auto: '🛺', erickshaw: '🛺', taxi: '🚖', cab: '🚕', courier: '📦',
};

const PARCEL_TYPES = ['Documents', 'Food', 'Clothes', 'Electronics', 'Medicines', 'Other'];

/**
 * Book a ride or a courier pick-and-drop.
 *
 * Both use the same two points and the same fare engine, so the only difference
 * is the parcel details and which vehicle options come back.
 */
const RideBookingPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { location, config } = useApp();
  const toast = useToast();

  const [serviceType, setServiceType] = useState(params.get('type') === 'courier' ? 'courier' : 'ride');
  const [pickup, setPickup] = useState(null);
  const [drop, setDrop] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [selected, setSelected] = useState(null);
  const [parcel, setParcel] = useState({
    type: 'Documents', weightKg: '', description: '', codAmount: '',
  });
  const [paymentMode, setPaymentMode] = useState('cod');

  // Same rule as checkout: online only appears once Razorpay keys exist.
  const onlineEnabled = Boolean(config && config.features && config.features.onlinePayment);

  const [picking, setPicking] = useState(null); // 'pickup' | 'drop' | null
  const [locating, setLocating] = useState(true);
  const [quoting, setQuoting] = useState(false);
  const [booking, setBooking] = useState(false);

  // Pickup defaults to where the customer is standing.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await requestLocationPermission();
      const fix = ok ? await getCurrentPosition() : null;
      if (cancelled) return;
      setPickup({
        coordinates: (fix && fix.coordinates) || [location.longitude, location.latitude],
        address: fix ? 'Current location' : (location.label || location.city),
      });
      setLocating(false);
    })();
    return () => { cancelled = true; };
  }, [location]);

  const getQuote = useCallback(async () => {
    if (!pickup || !drop) {
      toast.error('Set both a pickup and a drop point.', 'Where to?');
      return;
    }
    setQuoting(true);
    try {
      const res = await api.rides.estimate({
        serviceType,
        pickup,
        drop,
        parcel: serviceType === 'courier' ? { weightKg: Number(parcel.weightKg) || 0 } : undefined,
      });
      setEstimate(res.data);
      setSelected(res.data.options[0] && res.data.options[0].vehicleType);
    } catch (err) {
      toast.error(err.message, 'Could not get a fare');
    } finally { setQuoting(false); }
  }, [pickup, drop, serviceType, parcel.weightKg, toast]);

  const book = async () => {
    setBooking(true);
    try {
      const res = await api.rides.create({
        serviceType,
        vehicleType: selected,
        pickup,
        drop,
        paymentMode: onlineEnabled ? paymentMode : 'cod',
        parcel: serviceType === 'courier' ? {
          type: parcel.type,
          weightKg: Number(parcel.weightKg) || 0,
          description: parcel.description,
          codAmount: Number(parcel.codAmount) || 0,
        } : undefined,
      });
      const ride = res.data.ride;

      // Pay first: no driver is rung for an online trip until the payment is confirmed.
      if (onlineEnabled && paymentMode === 'online') {
        try {
          await startOnlinePayment({ refType: 'ride', refId: ride._id });
        } catch (err) {
          toast.error('We start looking for a driver once the payment goes through. Retry on the next page, or pay cash.',
            err.cancelled ? 'Payment cancelled' : 'Payment not completed');
        }
      }

      navigate(`/ride/${ride._id}`, { replace: true });
    } catch (err) {
      toast.error(err.message, 'Could not book');
    } finally { setBooking(false); }
  };

  if (locating) return <Loading text="Finding your location…" />;

  const chosen = estimate && estimate.options.find((o) => o.vehicleType === selected);

  const onPicked = (point) => {
    if (picking === 'pickup') setPickup(point); else setDrop(point);
    setEstimate(null);
    setSelected(null);
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <PageHeader
        title={serviceType === 'courier' ? 'Send a parcel' : 'Book a ride'}
        subtitle={serviceType === 'courier'
          ? 'Pick up and drop anywhere in the city'
          : 'Bike · Auto · Taxi · Cab'}
      />

      <div className="row" style={{ gap: 8, marginBottom: 16 }}>
        <Chip
          label="🚕 Ride"
          active={serviceType === 'ride'}
          onClick={() => { setServiceType('ride'); setEstimate(null); }}
        />
        <Chip
          label="📦 Courier"
          active={serviceType === 'courier'}
          onClick={() => { setServiceType('courier'); setEstimate(null); }}
        />
      </div>

      <Reveal className="card sh-sm">
        <div className="row" style={{ gap: 14, alignItems: 'stretch' }}>
          <div className="col" style={{ alignItems: 'center', paddingTop: 18 }}>
            <span style={{ width: 10, height: 10, borderRadius: 5, background: colors.success }} />
            <span className="grow" style={{ width: 2, background: colors.line, margin: '4px 0' }} />
            <span style={{ width: 10, height: 10, borderRadius: 5, background: colors.danger }} />
          </div>

          <div className="grow col" style={{ gap: 8 }}>
            <button
              type="button"
              onClick={() => setPicking('pickup')}
              style={{
                textAlign: 'left', background: colors.bg, border: `1px solid ${colors.line}`,
                borderRadius: 12, padding: 12,
              }}
            >
              <div className="t-tiny">PICKUP</div>
              <div className="t-body clamp-1" style={{ fontWeight: 600 }}>
                {(pickup && pickup.address) || 'Set pickup'}
              </div>
            </button>
            <button
              type="button"
              onClick={() => setPicking('drop')}
              style={{
                textAlign: 'left', background: colors.bg, border: `1px solid ${colors.line}`,
                borderRadius: 12, padding: 12,
              }}
            >
              <div className="t-tiny">DROP</div>
              <div
                className="t-body clamp-1"
                style={{ fontWeight: 600, color: drop ? colors.ink : colors.faint }}
              >
                {(drop && drop.address) || 'Search or use current location'}
              </div>
            </button>
          </div>
        </div>
      </Reveal>

      {serviceType === 'courier' ? (
        <Reveal delay={60} className="card sh-sm" style={{ marginTop: 16 }}>
          <h3 className="t-title" style={{ marginBottom: 10 }}>What are you sending?</h3>
          <div className="row wrap" style={{ gap: 8, marginBottom: 16 }}>
            {PARCEL_TYPES.map((t) => (
              <Chip
                key={t}
                label={t}
                active={parcel.type === t}
                onClick={() => setParcel({ ...parcel, type: t })}
              />
            ))}
          </div>
          <div className="form-row">
            <Input
              label="Weight (kg)"
              className="grow"
              inputMode="decimal"
              placeholder="2"
              value={parcel.weightKg}
              onChange={(e) => {
                setParcel({ ...parcel, weightKg: e.target.value.replace(/[^\d.]/g, '') });
                setEstimate(null);
              }}
            />
            <Input
              label="Cash to collect"
              className="grow"
              inputMode="numeric"
              placeholder="0"
              value={parcel.codAmount}
              onChange={(e) => setParcel({ ...parcel, codAmount: e.target.value.replace(/\D/g, '') })}
            />
          </div>
          <Input
            label="Receiver details / instructions"
            textarea
            rows={2}
            placeholder="Name, phone, floor…"
            value={parcel.description}
            onChange={(e) => setParcel({ ...parcel, description: e.target.value })}
            style={{ marginBottom: 0 }}
          />
        </Reveal>
      ) : null}

      {!estimate ? (
        <Button
          title="Check fare"
          icon="🔎"
          onClick={getQuote}
          loading={quoting}
          disabled={!drop}
          size="lg"
          full
          style={{ marginTop: 16 }}
        />
      ) : (
        <>
          <h3 className="t-title" style={{ marginTop: 22, marginBottom: 4 }}>
            {estimate.distanceKm} km · about {estimate.durationMin} min
          </h3>
          {estimate.distanceSource === 'estimate' ? (
            <div className="t-tiny" style={{ marginBottom: 10 }}>
              Distance is approximate until the Maps key is configured.
            </div>
          ) : null}

          <div className="col" style={{ gap: 8, marginTop: 10 }}>
            {estimate.options.map((o, i) => {
              const active = selected === o.vehicleType;
              return (
                <Reveal key={o.vehicleType} delay={stagger(i, 45)}>
                  <button
                    type="button"
                    onClick={() => setSelected(o.vehicleType)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left',
                      background: active ? colors.primaryLight : '#fff',
                      border: `1.4px solid ${active ? colors.primary : colors.line}`,
                      borderRadius: 16, padding: 14,
                      transition: 'border-color .16s ease, background .16s ease',
                    }}
                  >
                    <span style={{ fontSize: 28 }}>{VEHICLE_ICON[o.vehicleType] || '🚗'}</span>
                    <span className="grow">
                      <span className="t-title" style={{ display: 'block' }}>{o.label}</span>
                      <span className="t-tiny">
                        {o.capacity} seat{o.capacity > 1 ? 's' : ''} · {o.etaMinutes} min away
                      </span>
                    </span>
                    <span style={{ fontSize: 17, fontWeight: 800 }}>{currency(o.total)}</span>
                  </button>
                </Reveal>
              );
            })}
          </div>

          {chosen ? (
            <div className="card sh-sm" style={{ marginTop: 16 }}>
              <Row label="Base fare" value={currency(chosen.breakdown.baseFare)} />
              <Row
                label={`Distance (${chosen.breakdown.chargeableKm} km)`}
                value={currency(chosen.breakdown.distanceFare)}
              />
              <Row
                label={`Time (${estimate.durationMin} min)`}
                value={currency(chosen.breakdown.timeFare)}
              />
              {chosen.breakdown.nightSurcharge ? (
                <Row label="Night surcharge" value={currency(chosen.breakdown.nightSurcharge)} />
              ) : null}
              {chosen.breakdown.surgeAmount ? (
                <Row
                  label={`Surge ${chosen.breakdown.surgeMultiplier}x`}
                  value={currency(chosen.breakdown.surgeAmount)}
                />
              ) : null}
              {chosen.breakdown.weightCharge ? (
                <Row label="Extra weight" value={currency(chosen.breakdown.weightCharge)} />
              ) : null}
              <Row label="Platform fee" value={currency(chosen.breakdown.platformFee)} />
              <div className="t-tiny" style={{ marginTop: 8 }}>
                Waiting beyond the free minutes is added at the end of the trip.
              </div>
            </div>
          ) : null}

          {onlineEnabled ? (
            <>
              <div className="t-title" style={{ marginTop: 20, marginBottom: 8 }}>
                How do you want to pay?
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                {[
                  { key: 'cod', icon: '💵', title: 'Cash to driver', text: 'Pay at the end of the trip' },
                  { key: 'online', icon: '💳', title: 'Pay now', text: 'UPI, card or netbanking via Razorpay' },
                ].map((pm) => (
                  <button
                    key={pm.key}
                    type="button"
                    onClick={() => setPaymentMode(pm.key)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
                      padding: 14, borderRadius: 14, cursor: 'pointer',
                      background: paymentMode === pm.key ? colors.primaryLight : '#fff',
                      border: `1.4px solid ${paymentMode === pm.key ? colors.primary : colors.line}`,
                    }}
                  >
                    <span style={{ fontSize: 20 }}>{pm.icon}</span>
                    <span className="grow">
                      <span className="t-title" style={{ display: 'block' }}>{pm.title}</span>
                      <span className="t-tiny">{pm.text}</span>
                    </span>
                    <span style={{
                      width: 20, height: 20, borderRadius: 10,
                      border: `${paymentMode === pm.key ? 6 : 2}px solid ${paymentMode === pm.key ? colors.primary : colors.line}`,
                    }}
                    />
                  </button>
                ))}
              </div>
              {paymentMode === 'online' ? (
                <div className="t-tiny" style={{ marginTop: 8 }}>
                  🔒 Razorpay's secure page opens next. Waiting charges, if any, are
                  collected by the driver at the end.
                </div>
              ) : null}
            </>
          ) : null}

          <Button
            title={onlineEnabled && paymentMode === 'online'
              ? `Book & pay · ${currency((chosen && chosen.total) || 0)}`
              : `Book ${(chosen && chosen.label) || ''} · ${currency((chosen && chosen.total) || 0)}`}
            icon="⚡"
            onClick={book}
            loading={booking}
            size="lg"
            full
            style={{ marginTop: 16 }}
          />
          <Button
            title="Change locations"
            variant="light"
            full
            style={{ marginTop: 8 }}
            onClick={() => setEstimate(null)}
          />
        </>
      )}

      <PickLocationModal
        open={Boolean(picking)}
        title={picking === 'pickup' ? 'Set pickup point' : 'Set drop point'}
        onClose={() => setPicking(null)}
        onPicked={onPicked}
      />
    </div>
  );
};

export default RideBookingPage;
